import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useRef, type FormEvent, type ReactNode } from "react";
import { Clock, MapPin } from "lucide-react";
import { Page, SectionEyebrow } from "@/components/SiteLayout";
import hijZegtKom from "@/assets/hij_zegt_kom.mp4.asset.json";
import emailjs from "@emailjs/browser";

export const Route = createFileRoute("/inschrijven")({
  head: () => ({
    meta: [
      { title: "Inschrijven — Hi Coach" },
      { name: "description", content: "Schrijf je kind in bij Hi Coach. Vul de gegevens in en wij zorgen dat alles klaarstaat voor de eerste zondagochtend." },
      { property: "og:title", content: "Inschrijven bij Hi Coach" },
      { property: "og:description", content: "Jouw kind doet mee." },
      { property: "og:url", content: "/inschrijven" },
    ],
    links: [{ rel: "canonical", href: "/inschrijven" }],
  }),
  component: Inschrijven,
});

const inputCls = "w-full px-4 py-3 rounded-xl border-2 border-border bg-white font-medium focus:outline-none focus:border-rb-blue transition-colors";

const PAKKETTEN = [
  { key: "Gratis Proefles", naam: "Gratis Proefles", prijs: "Gratis", periode: "vrijblijvend", info: "Vrijblijvend kennismaken, geen verplichtingen", extra: "", c: "#22C55E" },
  { key: "Hi Instap", naam: "Hi Instap", prijs: "€ 50,00", periode: "/maand", info: "2 termijnen · 1 thema · 7 lessen", extra: "", c: "#FF0000" },
  { key: "Hi Sprong", naam: "Hi Sprong", prijs: "€ 45,00", periode: "/maand", info: "4 termijnen · 2 thema's · 14 lessen", extra: "", c: "#FFD700" },
  { key: "Hi Groei", naam: "Hi Groei", prijs: "€ 42,50", periode: "/maand", info: "6 termijnen · 3 thema's · 21 lessen", extra: "", c: "#FF7F00" },
  { key: "Hi Held", naam: "Hi Held", prijs: "€ 40,00", periode: "/maand", info: "10 termijnen · 5 thema's · 35 lessen", extra: "Jaarprogramma + diploma-uitreiking", c: "#1E90FF" },
] as const;

const GROEPEN = [
  { key: "Peuters", naam: "Peuters", leeftijd: "2 t/m 3 jaar", bg: "#FFE5E5", c: "#FF0000" },
  { key: "Kleuters", naam: "Kleuters", leeftijd: "4 t/m 6 jaar", bg: "#E0F1FF", c: "#1E90FF" },
] as const;

const OPVANG_OPTIES = ["Peuterspeelzaal", "Kinderdagverblijf", "Basisschool", "Geen"] as const;

const MATEN = [
  { key: "86/92", maat: "86/92", sub: "XXS" },
  { key: "98/104", maat: "98/104", sub: "XS" },
  { key: "110/116", maat: "110/116", sub: "S" },
  { key: "122/128", maat: "122/128", sub: "M" },
] as const;

const KLEUREN_PER_MAAT: Record<string, { naam: string; hex: string }[]> = {
  "86/92": [
    { naam: "Blauw", hex: "#1E90FF" },
    { naam: "Rood", hex: "#FF0000" },
  ],
  "98/104": [
    { naam: "Paars", hex: "#8B00FF" },
    { naam: "Rood", hex: "#FF0000" },
    { naam: "Oranje", hex: "#FF7F00" },
    { naam: "Groen", hex: "#00C851" },
    { naam: "Blauw", hex: "#1E90FF" },
    { naam: "Geel", hex: "#FFD700" },
  ],
  "110/116": [
    { naam: "Paars", hex: "#8B00FF" },
    { naam: "Rood", hex: "#FF0000" },
    { naam: "Oranje", hex: "#FF7F00" },
    { naam: "Groen", hex: "#00C851" },
    { naam: "Blauw", hex: "#1E90FF" },
    { naam: "Geel", hex: "#FFD700" },
  ],
  "122/128": [
    { naam: "Paars", hex: "#8B00FF" },
    { naam: "Rood", hex: "#FF0000" },
    { naam: "Oranje", hex: "#FF7F00" },
    { naam: "Groen", hex: "#00C851" },
    { naam: "Blauw", hex: "#1E90FF" },
    { naam: "Geel", hex: "#FFD700" },
  ],
};

const BETAALWIJZEN = [
  { key: "Ineens", label: "In 1 keer", sub: "Via automatische incasso of iDEAL" },
  { key: "Maandelijks", label: "Maandelijks", sub: "Via automatische incasso" },
] as const;

// Pakketkaart — zelfde opmaak als de kaarten op de Tarieven-pagina (bg-white, border-t-8, shadow-xl,
// rounded-3xl, p-7), nu klikbaar via een verborgen radio-input. "Geselecteerd" hergebruikt exact de
// badge-stijl van het "Aanbevolen"-label op Tarieven.
function PakketCard({ p, active, onSelect }: { p: (typeof PAKKETTEN)[number]; active: boolean; onSelect: () => void }) {
  return (
    <label
      className="relative cursor-pointer rounded-3xl p-7 shadow-xl flex flex-col border-t-8 bg-white transition-shadow"
      style={{
        borderTopColor: p.c,
        color: "#1a1a1a",
        ...(active ? { boxShadow: `0 0 0 3px ${p.c}, 0 20px 25px -5px rgb(0 0 0 / 0.15)` } : {}),
      }}
    >
      <input type="radio" name="pakket" value={p.key} onClick={onSelect} required className="sr-only" />
      {active && (
        <span className="absolute -top-3 right-6 bg-foreground text-white text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">
          Geselecteerd
        </span>
      )}
      <h3 className="text-2xl font-extrabold">{p.naam}</h3>
      <p className="mt-3 text-sm text-muted-foreground flex-1">{p.info}</p>
      {p.extra && <p className="mt-2 text-sm font-extrabold" style={{ color: p.c }}>{p.extra}</p>}
      <p className="mt-4 text-3xl font-extrabold">{p.prijs}<span className="text-sm font-bold"> {p.periode}</span></p>
    </label>
  );
}

// Groepskaart — zelfde opmaak als de leeftijdskaarten op de Lessen-pagina (zachte kleurvlakken,
// rounded-3xl p-8, tijd-badge), nu klikbaar via een verborgen radio-input.
function GroupCard({ g, active, onSelect }: { g: (typeof GROEPEN)[number]; active: boolean; onSelect: () => void }) {
  return (
    <label
      className="relative cursor-pointer rounded-3xl p-8 block transition-shadow"
      style={{ background: g.bg, ...(active ? { boxShadow: `0 0 0 3px ${g.c}` } : {}) }}
    >
      <input type="radio" name="groep" value={g.key} onClick={onSelect} required className="sr-only" />
      {active && (
        <span className="absolute -top-3 right-6 bg-foreground text-white text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">
          Geselecteerd
        </span>
      )}
      <h3 className="text-2xl font-extrabold">{g.naam}</h3>
      <p className="mt-1 text-muted-foreground">{g.leeftijd}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold shadow-sm">
          <Clock size={16} /> Zondag 09:30 – 10:30 uur
        </span>
      </div>
    </label>
  );
}

// Compacte selecteerbare knop (radio-pill) voor korte keuzes zoals geslacht of opvangtype
function PillOption({
  name,
  value,
  active,
  onSelect,
  children,
}: {
  name: string;
  value: string;
  active: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <label
      className={`cursor-pointer rounded-2xl border-2 p-4 text-center font-extrabold transition-all ${
        active ? "bg-foreground text-white border-foreground" : "border-border hover:bg-secondary"
      }`}
    >
      <input type="radio" name={name} value={value} onClick={onSelect} required className="sr-only" />
      {children}
    </label>
  );
}

// Kleurstaal — compacte swatch-optie voor de shirtkleur.
function KleurOption({
  name,
  value,
  hex,
  active,
  onSelect,
}: {
  name: string;
  value: string;
  hex: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 cursor-pointer rounded-2xl border-2 p-3 transition-all ${
        active ? "border-foreground bg-secondary" : "border-border hover:bg-secondary"
      }`}
    >
      <input type="radio" name={name} value={value} onClick={onSelect} required className="sr-only" />
      <span className="h-6 w-6 rounded-full border border-border shrink-0" style={{ background: hex }} />
      <span className="font-extrabold text-sm">{value}</span>
    </label>
  );
}

// Betaalkaart — zelfde selectiepatroon (verborgen radio + "Geselecteerd"-badge) als de pakket-/groepskaarten.
function BetaalCard({
  label,
  sub,
  value,
  active,
  onSelect,
}: {
  label: string;
  sub: string;
  value: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`relative cursor-pointer rounded-3xl border-2 p-6 transition-all ${
        active ? "border-foreground bg-secondary shadow-md" : "border-border hover:bg-secondary"
      }`}
    >
      <input type="radio" name="betaalwijze" value={value} onClick={onSelect} required className="sr-only" />
      {active && (
        <span className="absolute -top-3 right-6 bg-foreground text-white text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">
          Geselecteerd
        </span>
      )}
      <div className="font-extrabold text-lg">{label}</div>
      <div className="mt-1 text-sm text-muted-foreground">{sub}</div>
    </label>
  );
}

// Akkoord-checkbox — grotere klikzone rond de native checkbox, consistent met de rest van het formulier.
function ConsentCheckbox({ name, required, children }: { name: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer rounded-2xl border-2 border-border p-4 hover:bg-secondary transition-colors">
      <input type="checkbox" name={name} required={required} className="mt-1 h-5 w-5 accent-rb-green shrink-0" />
      <span className="text-sm font-medium">{children}</span>
    </label>
  );
}

function InschrijvenContent() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittedName = useRef("");

  // Sectie 1
  const [pakket, setPakket] = useState<string>("");
  // Sectie 2
  const [groep, setGroep] = useState<string>("");
  // Sectie 3
  const [geslacht, setGeslacht] = useState<string>("");
  const [opvangType, setOpvangType] = useState<string>("");
  // Sectie 5
  const [maat, setMaat] = useState<string>("");
  const [kleur, setKleur] = useState<string>("");
  // Sectie 6
  const [betaalwijze, setBetaalwijze] = useState<string>("");

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending) return;
    setError(null);
    setSending(true);
    const data = new FormData(e.currentTarget);
    const voornaam = data.get("voornaam") as string;
    const achternaam = data.get("achternaam") as string;
    submittedName.current = voornaam;
    try {
      const email = data.get("email") as string;
      const alleVelden = {
        voornaam,
        achternaam,
        email,
        telefoon: data.get("telefoon"),
        adres: `${data.get("straatHuisnummer")}, ${data.get("postcodePlaats")}`,
        kindVoornaam: data.get("kindVoornaam"),
        kindAchternaam: data.get("kindAchternaam"),
        geboortedatum: data.get("geboortedatum"),
        geslacht,
        opvangType,
        opvangNaam: data.get("opvangNaam"),
        pakket,
        groep,
        maat,
        kleur,
        betaalwijze,
        iban: data.get("iban"),
        rekeninghouder: data.get("rekeninghouder"),
        opmerkingen: data.get("opmerkingen"),
        beeldmateriaal: data.get("akkoordBeeldmateriaal") ? "Ja" : "Nee",
      };
      // Bevestiging naar klant
      await emailjs.send(
        "service_gnizfwq",
        "template_ayyj4n2",
        { name: `${voornaam} ${achternaam}`, ...alleVelden },
        "2BUPqpQPgvYkZBETf",
      );
      // Notificatie naar info@hicoachclub.nl
      await emailjs.send(
        "service_gnizfwq",
        "template_qz61uj6",
        alleVelden,
        "2BUPqpQPgvYkZBETf",
      );
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      setSubmitted(true);
    } catch {
      setError("Geen verbinding. Controleer je internet en probeer het opnieuw.");
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <div className="text-6xl">🎉</div>
          <h1 className="mt-6 text-4xl font-extrabold text-gradient-rainbow">Inschrijving ontvangen!</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            We nemen zo spoedig mogelijk persoonlijk contact met je op.
          </p>
          <Link to="/" className="btn-cta mt-8 inline-flex">Terug naar home</Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="pt-24 pb-10 bg-white">
        <div className="mx-auto max-w-3xl px-6">
          <SectionEyebrow color="var(--rb-green)">Inschrijven</SectionEyebrow>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
            Jouw kind <span style={{ color: "var(--rb-green)" }}>doet mee</span>
          </h1>
          <p className="mt-6 text-lg">
            Goed dat je er bent. Vul hieronder de gegevens in en wij zorgen dat alles klaarstaat voor de eerste zondagochtend.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <form onSubmit={submit} className="mx-auto max-w-5xl px-6 space-y-14">

          {/* Sectie 1 — Kies je lespakket */}
          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-orange)" }}>1. Kies je lespakket</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {PAKKETTEN.map((p) => (
                <PakketCard key={p.key} p={p} active={pakket === p.key} onSelect={() => setPakket(p.key)} />
              ))}
            </div>
          </div>

          {/* Sectie 2 — Groepskeuze & leslocatie */}
          <div>
            <h2 className="text-xl font-extrabold mb-2" style={{ color: "var(--rb-blue)" }}>2. Groepskeuze &amp; leslocatie</h2>
            <p className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
              <MapPin size={16} /> Sporthal Portland – De Beurs 41, Rhoon
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {GROEPEN.map((g) => (
                <GroupCard key={g.key} g={g} active={groep === g.key} onSelect={() => setGroep(g.key)} />
              ))}
            </div>
          </div>

          {/* Sectie 3 — Gegevens deelnemer (kind) */}
          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-red)" }}>3. Gegevens deelnemer (kind)</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required name="kindVoornaam" placeholder="Voornaam" className={inputCls} />
              <input required name="kindAchternaam" placeholder="Achternaam" className={inputCls} />
              <input required name="geboortedatum" type="date" aria-label="Geboortedatum" className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <PillOption name="geslacht" value="Jongen" active={geslacht === "Jongen"} onSelect={() => setGeslacht("Jongen")}>Jongen</PillOption>
                <PillOption name="geslacht" value="Meisje" active={geslacht === "Meisje"} onSelect={() => setGeslacht("Meisje")}>Meisje</PillOption>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-extrabold mb-3">Basisschool of kinderopvang</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {OPVANG_OPTIES.map((o) => (
                  <PillOption key={o} name="opvangType" value={o} active={opvangType === o} onSelect={() => setOpvangType(o)}>{o}</PillOption>
                ))}
              </div>
              {opvangType && opvangType !== "Geen" && (
                <input required name="opvangNaam" placeholder={`Naam van de ${opvangType.toLowerCase()}`} className={`${inputCls} mt-3`} />
              )}
            </div>
          </div>

          {/* Sectie 4 — Gegevens ouder/verzorger */}
          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-violet)" }}>4. Gegevens ouder/verzorger</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required name="voornaam" placeholder="Voornaam" className={inputCls} />
              <input required name="achternaam" placeholder="Achternaam" className={inputCls} />
              <input required name="straatHuisnummer" placeholder="Straatnaam + huisnummer" className={inputCls} />
              <input required name="postcodePlaats" placeholder="Postcode + plaats" className={inputCls} />
              <input required name="telefoon" type="tel" placeholder="Telefoonnummer" className={inputCls} />
              <input required name="email" type="email" placeholder="E-mailadres" className={inputCls} />
            </div>
          </div>

          {/* Sectie 5 — Hi Coach Club sportshirtje */}
          {pakket !== "Gratis Proefles" && (
            <div>
              <h2 className="text-xl font-extrabold mb-2" style={{ color: "var(--rb-pink)" }}>5. Hi Coach Club sportshirtje</h2>
              <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
                Bij de inschrijving ontvangt ieder nieuw lid het officiële Hi Coach Club sportshirtje (inbegrepen in de eenmalige inschrijfkosten van €49,95).
              </p>
              <h3 className="font-extrabold mb-3">Maat</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {MATEN.map((m) => (
                  <PillOption
                    key={m.key}
                    name="maat"
                    value={m.key}
                    active={maat === m.key}
                    onSelect={() => { setMaat(m.key); setKleur(""); }}
                  >
                    <div>{m.maat}</div>
                    <div className="text-xs font-normal opacity-70">{m.sub}</div>
                  </PillOption>
                ))}
              </div>

              {maat && (
                <div className="mt-6">
                  <h3 className="font-extrabold mb-3">Kleur</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {KLEUREN_PER_MAAT[maat].map((k) => (
                      <KleurOption
                        key={k.naam}
                        name="kleur"
                        value={k.naam}
                        hex={k.hex}
                        active={kleur === k.naam}
                        onSelect={() => setKleur(k.naam)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sectie 6 — Betalingsgegevens & incasso */}
          {pakket !== "Gratis Proefles" && (
            <div>
              <h2 className="text-xl font-extrabold mb-2" style={{ color: "var(--rb-green)" }}>6. Betalingsgegevens &amp; incasso</h2>
              <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
                De eenmalige inschrijfkosten bedragen €49,95 (inclusief het Hi Coach Club sportshirt).
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {BETAALWIJZEN.map((b) => (
                  <BetaalCard key={b.key} value={b.key} label={b.label} sub={b.sub} active={betaalwijze === b.key} onSelect={() => setBetaalwijze(b.key)} />
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <input required name="iban" placeholder="IBAN" className={inputCls} />
                <input required name="rekeninghouder" placeholder="Naam rekeninghouder" className={inputCls} />
              </div>
            </div>
          )}

          {/* Sectie 7 — Opmerkingen & bijzonderheden */}
          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-cyan)" }}>7. Opmerkingen &amp; bijzonderheden</h2>
            <label htmlFor="opmerkingen" className="block font-bold text-sm mb-2">
              Belangrijke informatie voor de coach (bijvoorbeeld allergieën, medische of lichamelijke aandachtspunten).
            </label>
            <textarea id="opmerkingen" name="opmerkingen" rows={4} className={`${inputCls} resize-y`} />
          </div>

          {/* Sectie 8 — Akkoord & bevestiging */}
          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-indigo)" }}>8. Akkoord &amp; bevestiging</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-extrabold mb-2">Algemene Voorwaarden &amp; Privacyverklaring</h3>
                <ConsentCheckbox name="akkoordVoorwaarden" required>
                  Ik ga akkoord met de{" "}
                  <Link
                    to="/algemene-voorwaarden"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-bold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Algemene Voorwaarden
                  </Link>{" "}
                  en de{" "}
                  <Link
                    to="/privacyverklaring"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-bold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacyverklaring
                  </Link>{" "}
                  van Hi Coach Club.
                </ConsentCheckbox>
              </div>
              <div>
                <h3 className="font-extrabold mb-2">
                  Beeldmateriaal &amp; Media <span className="font-normal text-muted-foreground">(Optioneel)</span>
                </h3>
                <ConsentCheckbox name="akkoordBeeldmateriaal">
                  Hi Coach Club mag foto's en korte video's die tijdens de lessen worden gemaakt gebruiken voor de website en sociale media.
                </ConsentCheckbox>
              </div>
            </div>

            <div className="mt-8">
              <button type="submit" disabled={sending} className="btn-cta w-full sm:w-auto disabled:opacity-60">
                {sending ? "Bezig met versturen..." : "Verstuur inschrijving"}
              </button>
              {error && (
                <p className="mt-4 text-sm font-bold" style={{ color: "var(--rb-red)" }}>{error}</p>
              )}
              <p className="mt-4 text-sm text-muted-foreground">
                Heb je eerst nog een vraag? <a className="underline font-bold" href="https://wa.me/31626716754">App ons via WhatsApp</a>, we reageren snel.
              </p>
            </div>
          </div>
        </form>
      </section>

      <section className="py-16 flex justify-center px-6">
        <div className="p-[4px] rounded-3xl gradient-rainbow shadow-2xl" style={{ maxWidth: 260 }}>
          <div className="rounded-[calc(1.5rem-4px)] overflow-hidden">
            <video
              className="w-full h-auto block"
              src={hijZegtKom.url}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
          </div>
        </div>
      </section>
    </>
  );
}

function Inschrijven() {
  return (
    <Page>
      <InschrijvenContent />
    </Page>
  );
}
